if (!customElements.get('new-plan-picker')) {
  const plainDollarCurrencies = new Set([
    'USD', 'CAD', 'AUD', 'NZD', 'SGD', 'HKD', 'TWD', 'BBD',
    'BMD', 'BND', 'BSD', 'BZD', 'FJD', 'GYD', 'JMD', 'KYD',
    'LRD', 'NAD', 'SBD', 'SRD', 'TTD', 'XCD', 'ZWL',
  ]);

  class NewPlanPicker extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const dataElement = this.querySelector('[data-plan-picker-data]');
      if (!dataElement) return;

      try {
        this.config = JSON.parse(dataElement.textContent);
      } catch (error) {
        console.error('New Plan Picker: invalid configuration', error);
        return;
      }

      this.primaryDiscountCode = String(
        this.config.primaryDiscountCode || this.config.subscriptionDiscountCode || ''
      ).trim();
      this.subscriptionFreeShippingDiscountCode = String(
        this.config.subscriptionFreeShippingDiscountCode || ''
      ).trim();
      this.quantityDiscountCode = String(this.config.quantityDiscountCode || '').trim();
      this.quantityDiscountMinimum = this.clampDiscountMinimum(
        Number(this.config.quantityDiscountMinimum || 2)
      );
      this.giftFeatureEnabled = this.parseBoolean(this.config.giftFeatureEnabled);
      this.mysteryGiftVariantId = this.normalizeVariantId(this.config.mysteryGiftVariantId);
      this.mysteryGiftAvailable = this.parseBoolean(this.config.mysteryGiftAvailable);
      this.giftDiscountCode = String(this.config.giftDiscountCode || '').trim();
      this.subscriptionOnly = this.parseBoolean(this.dataset.subscriptionOnly);

      this.form = document.getElementById(this.dataset.productFormId);
      if (!this.form) return;

      this.variantInput = this.form.querySelector('[name="id"]');
      this.quantityInput = this.form.querySelector('[name="quantity"]');
      if (!this.quantityInput) {
        this.quantityInput = document.createElement('input');
        this.quantityInput.type = 'hidden';
        this.quantityInput.name = 'quantity';
        this.form.appendChild(this.quantityInput);
      }

      this.quantityButtons = Array.from(this.querySelectorAll('[data-kids]'));
      this.purchaseChoices = Array.from(this.querySelectorAll('[data-purchase-choice]'));
      this.quantityBadge = this.querySelector('[data-quantity-badge]');
      this.planNotice = this.querySelector('[data-plan-notice]');
      this.onetimeDetails = this.querySelector('[data-onetime-details]');

      this.quantityButtons.forEach((button) => {
        button.addEventListener('click', () => this.setKids(Number(button.dataset.kids)));
      });

      this.purchaseChoices.forEach((choice) => {
        choice.addEventListener('click', () => this.setMode(choice.dataset.purchaseChoice));
        choice.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.setMode(choice.dataset.purchaseChoice);
          }
        });
      });

      if (this.variantInput) {
        this.variantObserver = new MutationObserver(() => this.handleVariantChange());
        this.variantObserver.observe(this.variantInput, {
          attributes: true,
          attributeFilter: ['value'],
        });
        this.form.addEventListener('change', () => this.handleVariantChange());
      }

      this.discountBridge = this.installCartDiscountBridge();
      this.form.addEventListener('submit', (event) => {
        this.applyFormState();
        if (this.blockUnavailableSubscription(event)) return;
        this.queuePurchaseDiscount();
      }, true);
      this.form.addEventListener('click', (event) => {
        const secondaryCheckoutButton = event.target.closest('[data-skip-cart-button="true"]');
        if (!secondaryCheckoutButton || !this.form.contains(secondaryCheckoutButton)) return;
        this.applyFormState();
        if (this.blockUnavailableSubscription(event)) return;
        this.queuePurchaseDiscount();
      }, true);

      this.kids = this.clampKids(Number(this.dataset.defaultKids || 1));
      this.mode = this.subscriptionOnly
        ? 'subscription'
        : (this.dataset.defaultMode === 'onetime' ? 'onetime' : 'subscription');
      if (!this.subscriptionOnly && !this.hasSellingPlan() && this.mode === 'subscription') {
        this.mode = 'onetime';
      }

      this.setKids(this.kids);
      this.setMode(this.mode);
    }

    disconnectedCallback() {
      if (this.variantObserver) this.variantObserver.disconnect();
    }

    installCartDiscountBridge() {
      const bridgeKey = '__newPlanPickerDiscountBridge';
      if (window[bridgeKey]) return window[bridgeKey];
      if (typeof window.fetch !== 'function') return null;

      const originalFetch = window.fetch.bind(window);
      const bridge = {
        pending: null,
        originalFetch,
      };

      window.fetch = async (input, init) => {
        const pending = bridge.pending;
        const requestUrl = typeof input === 'string' ? input : (input && input.url) || '';
        const isPendingRequest = Boolean(
          pending
          && pending.picker
          && pending.expiresAt > Date.now()
          && pending.picker.isCartAddUrl(requestUrl)
          && pending.picker.cartAddMatchesPurchase(init && init.body, pending)
        );

        if (!isPendingRequest) {
          if (pending && pending.expiresAt <= Date.now()) bridge.pending = null;
          return originalFetch(input, init);
        }

        bridge.pending = null;
        let giftRequest = {
          input,
          init,
          giftExpected: false,
          giftInjected: false,
          giftAlreadyInCart: false,
        };

        if (pending.mode === 'subscription' && pending.giftActive) {
          try {
            giftRequest = await pending.picker.prepareGiftCartAdd(
              input,
              init,
              pending,
              originalFetch
            );
          } catch (error) {
            console.error('New Plan Picker: the mystery gift could not be prepared', error);
            pending.picker.reportGiftIssue(
              'Your free mystery gift could not be added. You were not charged for it.',
              { stage: 'prepare', error }
            );
          }
        }

        let preservedExistingGift = false;
        if (!pending.giftActive) {
          try {
            preservedExistingGift = await pending.picker.reconcileInactiveGiftCart(
              pending,
              originalFetch
            );
            if (!preservedExistingGift) {
              await pending.picker.removeDiscountCodes([pending.giftDiscountCode], originalFetch);
            }
            pending.picker.clearGiftIssue();
          } catch (error) {
            pending.picker.reportGiftIssue(
              'Checkout was stopped because an earlier mystery gift could not be safely removed. Please try again.',
              { stage: 'pre-add-cleanup', error }
            );
            throw error;
          }
        }

        let response = await originalFetch(giftRequest.input, giftRequest.init);
        if (!response.ok && giftRequest.giftInjected) {
          console.warn('New Plan Picker: retrying the main product without the mystery gift');
          response = await originalFetch(input, init);
          giftRequest.giftExpected = false;
          giftRequest.giftInjected = false;
          pending.picker.reportGiftIssue(
            'Your free mystery gift was unavailable and was not added. You were not charged for it.',
            { stage: 'cart-add-fallback', status: response.status }
          );
        }

        if (!response.ok) return response;

        try {
          if (!giftRequest.giftExpected) {
            preservedExistingGift = await pending.picker.reconcileInactiveGiftCart(
              pending,
              originalFetch
            );
          }

          const giftShouldRemain = giftRequest.giftExpected || preservedExistingGift;
          const effectiveCodes = pending.codes.filter((code) => (
            giftShouldRemain
            || String(code || '').toLowerCase() !== String(pending.giftDiscountCode || '').toLowerCase()
          ));
          if (
            giftShouldRemain
            && pending.giftDiscountCode
            && !effectiveCodes.some((code) => (
              String(code || '').toLowerCase() === String(pending.giftDiscountCode).toLowerCase()
            ))
          ) effectiveCodes.push(pending.giftDiscountCode);

          await pending.picker.applyPurchaseDiscounts(
            effectiveCodes,
            originalFetch,
            giftShouldRemain ? [] : [pending.giftDiscountCode]
          );

          if (giftShouldRemain) {
            const giftIsFree = await pending.picker.validateMysteryGiftIsFree(
              pending.giftVariantId,
              originalFetch
            );

            if (!giftIsFree) {
              await pending.picker.removeMarkedGiftLines(originalFetch);
              await pending.picker.removeDiscountCodes([pending.giftDiscountCode], originalFetch);
              pending.picker.reportGiftIssue(
                'Your free mystery gift could not be discounted and was removed. You were not charged for it.',
                { stage: 'validate-discount' }
              );
            } else {
              pending.picker.clearGiftIssue();
            }
          }
        } catch (error) {
          console.error('New Plan Picker: purchase discounts could not be applied', error);
          try {
            await pending.picker.removeMarkedGiftLines(originalFetch);
            await pending.picker.removeDiscountCodes([pending.giftDiscountCode], originalFetch);
          } catch (cleanupError) {
            console.error('New Plan Picker: the unsafe mystery gift could not be cleaned up', cleanupError);
            pending.picker.reportGiftIssue(
              'Checkout was stopped because the mystery gift could not be safely removed. Please try again.',
              { stage: 'cleanup', error: cleanupError }
            );
            throw cleanupError;
          }
          if (pending.mode === 'subscription' && giftRequest.giftExpected) {
            pending.picker.reportGiftIssue(
              'Your free mystery gift could not be discounted and was removed. You were not charged for it.',
              { stage: 'discount', error }
            );
          }
        }

        return response;
      };

      window[bridgeKey] = bridge;
      return bridge;
    }

    queuePurchaseDiscount() {
      if (!this.discountBridge) return;

      const codes = this.getPurchaseDiscountCodes();
      const variant = this.getVariant();
      const variantId = this.getVariantId();
      const isSubscription = this.mode === 'subscription';
      const gift = this.getMysteryGiftConfiguration();

      if (
        !variantId
        || (this.mode !== 'subscription' && this.mode !== 'onetime')
        || (isSubscription && (!variant || !variant.sellingPlanId))
      ) {
        if (this.discountBridge.pending && this.discountBridge.pending.picker === this) {
          this.discountBridge.pending = null;
        }
        return;
      }

      this.discountBridge.pending = {
        picker: this,
        codes,
        mode: this.mode,
        variantId,
        sellingPlanId: isSubscription ? String(variant.sellingPlanId) : '',
        quantity: String(this.kids || 1),
        giftActive: isSubscription && gift.active,
        giftVariantId: gift.variantId,
        giftDiscountCode: gift.discountCode,
        expiresAt: Date.now() + 10000,
      };
    }

    getPurchaseDiscountCodes() {
      const codes = [];
      const seen = new Set();
      const addCode = (value, label) => {
        const code = String(value || '').trim();
        if (!code) return;
        if (code.includes(',')) {
          console.error(`New Plan Picker: the ${label} cannot contain a comma`);
          return;
        }

        const key = code.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        codes.push(code);
      };

      addCode(this.primaryDiscountCode, 'primary discount code');
      if ((this.kids || 1) >= this.quantityDiscountMinimum) {
        addCode(this.quantityDiscountCode, 'quantity discount code');
      }
      if (this.mode === 'subscription') {
        addCode(this.subscriptionFreeShippingDiscountCode, 'subscription free-shipping discount code');
        const gift = this.getMysteryGiftConfiguration();
        if (gift.active) addCode(gift.discountCode, 'mystery-gift discount code');
      }

      return codes;
    }

    isCartAddUrl(value) {
      if (!value) return false;
      try {
        const url = new URL(value, window.location.origin);
        return /\/cart\/add(?:\.js)?\/?$/.test(url.pathname);
      } catch (error) {
        return /\/cart\/add(?:\.js)?(?:[?#]|$)/.test(String(value));
      }
    }

    cartAddMatchesPurchase(body, pending) {
      if (
        !body
        || !pending
        || !pending.variantId
        || (pending.mode !== 'subscription' && pending.mode !== 'onetime')
      ) return false;

      const candidates = [];
      const addCandidate = (value) => {
        if (!value || typeof value !== 'object') return;
        candidates.push({
          variantId: value.id ?? value.variant_id,
          sellingPlanId: value.selling_plan ?? value.selling_plan_id ?? '',
          quantity: value.quantity ?? 1,
        });
      };

      const readEntries = (entries) => {
        const root = {};
        const indexed = new Map();
        const assign = (target, field, value) => {
          const normalizedField = field === 'variant_id'
            ? 'id'
            : (field === 'selling_plan_id' ? 'selling_plan' : field);
          target[normalizedField] = value;
        };

        for (const [key, value] of entries) {
          if (/^(id|variant_id|quantity|selling_plan|selling_plan_id)$/.test(key)) {
            assign(root, key, value);
            continue;
          }

          const itemMatch = key.match(/^items\[(\d+)\]\[(id|variant_id|quantity|selling_plan|selling_plan_id)\]$/);
          if (!itemMatch) continue;
          const index = itemMatch[1];
          if (!indexed.has(index)) indexed.set(index, {});
          assign(indexed.get(index), itemMatch[2], value);
        }

        if (Object.keys(root).length) addCandidate(root);
        indexed.forEach(addCandidate);
      };

      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        readEntries(body.entries());
      } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        readEntries(body.entries());
      } else if (typeof body === 'string') {
        try {
          const parsed = JSON.parse(body);
          if (Array.isArray(parsed.items)) parsed.items.forEach(addCandidate);
          else addCandidate(parsed);
        } catch (error) {
          try {
            readEntries(new URLSearchParams(body).entries());
          } catch (searchError) {
            return false;
          }
        }
      } else if (typeof body === 'object') {
        if (Array.isArray(body.items)) body.items.forEach(addCandidate);
        else addCandidate(body);
      }

      return candidates.some((candidate) => {
        const candidatePlanId = String(candidate.sellingPlanId || '');
        const pendingPlanId = String(pending.sellingPlanId || '');
        const planMatches = pending.mode === 'subscription'
          ? Boolean(pendingPlanId) && candidatePlanId === pendingPlanId
          : candidatePlanId === '';

        return (
          String(candidate.variantId || '') === String(pending.variantId)
          && String(candidate.quantity || 1) === String(pending.quantity || 1)
          && planMatches
        );
      });
    }

    getMysteryGiftConfiguration() {
      return {
        active: Boolean(
          this.giftFeatureEnabled
          && this.mysteryGiftAvailable
          && this.mysteryGiftVariantId
        ),
        variantId: this.mysteryGiftVariantId,
        discountCode: this.giftDiscountCode,
      };
    }

    getGiftProperties() {
      return {
        _new_plan_picker_gift: 'true',
        _new_plan_picker_source: 'new-plan-picker',
      };
    }

    isMarkedGiftItem(item) {
      const properties = item && item.properties;
      return Boolean(
        properties
        && String(properties._new_plan_picker_gift || '') === 'true'
        && String(properties._new_plan_picker_source || '') === 'new-plan-picker'
      );
    }

    getMarkedGiftItems(cart) {
      if (!cart || !Array.isArray(cart.items)) return [];
      return cart.items.filter((item) => this.isMarkedGiftItem(item));
    }

    async getCart(fetchRequest) {
      const response = await fetchRequest(`${this.getCartApiRoot()}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Shopify returned ${response.status} while reading the cart`);
      return response.json();
    }

    async updateCartLines(updates, fetchRequest) {
      if (!updates || Object.keys(updates).length === 0) return;
      const response = await fetchRequest(`${this.getCartApiRoot()}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error(`Shopify returned ${response.status} while updating gift lines`);
    }

    async prepareGiftCartAdd(input, init, pending, fetchRequest) {
      const cart = await this.getCart(fetchRequest);
      const markedItems = this.getMarkedGiftItems(cart);
      const matchingItems = markedItems.filter((item) => (
        String(item.variant_id || item.id || '') === String(pending.giftVariantId)
      ));
      const keptItem = matchingItems[0] || null;
      const updates = {};

      markedItems.forEach((item) => {
        if (!item.key) return;
        updates[item.key] = item === keptItem ? 1 : 0;
      });
      await this.updateCartLines(updates, fetchRequest);

      if (keptItem) {
        return {
          input,
          init,
          giftExpected: true,
          giftInjected: false,
          giftAlreadyInCart: true,
        };
      }

      const giftItem = {
        id: String(pending.giftVariantId),
        quantity: 1,
        properties: this.getGiftProperties(),
      };
      const augmented = this.augmentCartAddBody(init && init.body, giftItem);
      if (!augmented) throw new Error('Unsupported cart request body; the gift was not injected');

      const nextInit = Object.assign({}, init || {}, { body: augmented.body });
      if (augmented.json) {
        const headers = new Headers((init && init.headers) || {});
        if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
        nextInit.headers = headers;
      }

      return {
        input,
        init: nextInit,
        giftExpected: true,
        giftInjected: true,
        giftAlreadyInCart: false,
      };
    }

    augmentCartAddBody(body, giftItem) {
      if (!body) return null;

      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        const augmented = this.augmentEntryBody(body, giftItem, true);
        return augmented ? { body: augmented, json: false } : null;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        const augmented = this.augmentEntryBody(body, giftItem, false);
        return augmented ? { body: augmented, json: false } : null;
      }
      if (typeof body === 'string') {
        try {
          const parsed = JSON.parse(body);
          const payload = this.augmentJsonPayload(parsed, giftItem);
          return payload ? { body: JSON.stringify(payload), json: true } : null;
        } catch (error) {
          const params = new URLSearchParams(body);
          const augmented = this.augmentEntryBody(params, giftItem, false);
          return augmented ? { body: augmented, json: false } : null;
        }
      }
      if (typeof body === 'object') {
        const payload = this.augmentJsonPayload(body, giftItem);
        return payload ? { body: JSON.stringify(payload), json: true } : null;
      }
      return null;
    }

    augmentJsonPayload(payload, giftItem) {
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
      // Shopify displays later-added lines first, so send the gift before the main product.
      if (Array.isArray(payload.items)) {
        return Object.assign({}, payload, { items: [giftItem].concat(payload.items) });
      }

      const lineFields = ['id', 'variant_id', 'quantity', 'selling_plan', 'selling_plan_id', 'properties'];
      const mainItem = {};
      const envelope = Object.assign({}, payload);
      lineFields.forEach((field) => {
        if (!Object.prototype.hasOwnProperty.call(payload, field)) return;
        mainItem[field] = payload[field];
        delete envelope[field];
      });
      if (!mainItem.id && !mainItem.variant_id) return null;
      envelope.items = [giftItem, mainItem];
      return envelope;
    }

    augmentEntryBody(body, giftItem, useFormData) {
      const entries = Array.from(body.entries());
      const indexedEntries = entries.filter(([key]) => /^items\[(\d+)\]\[/.test(key));
      const createBody = () => (useFormData ? new FormData() : new URLSearchParams());
      const result = createBody();
      const append = (key, value) => result.append(key, value);

      if (indexedEntries.length) {
        this.appendGiftEntryFields(append, 0, giftItem);
        entries.forEach(([key, value]) => {
          const match = key.match(/^items\[(\d+)\](.*)$/);
          if (match) append(`items[${Number(match[1]) + 1}]${match[2]}`, value);
          else append(key, value);
        });
        return result;
      }

      if (!entries.some(([key]) => key === 'id' || key === 'variant_id')) return null;
      const lineFieldPattern = /^(id|variant_id|quantity|selling_plan|selling_plan_id)$/;
      this.appendGiftEntryFields(append, 0, giftItem);
      entries.forEach(([key, value]) => {
        const lineMatch = key.match(lineFieldPattern);
        const propertyMatch = key.match(/^properties\[(.*)\]$/);
        if (lineMatch) append(`items[1][${lineMatch[1]}]`, value);
        else if (propertyMatch) append(`items[1][properties][${propertyMatch[1]}]`, value);
        else append(key, value);
      });
      return result;
    }

    appendGiftEntryFields(append, index, giftItem) {
      append(`items[${index}][id]`, String(giftItem.id));
      append(`items[${index}][quantity]`, '1');
      Object.entries(giftItem.properties || {}).forEach(([key, value]) => {
        append(`items[${index}][properties][${key}]`, String(value));
      });
    }

    async removeMarkedGiftLines(fetchRequest) {
      const cart = await this.getCart(fetchRequest);
      const updates = {};
      this.getMarkedGiftItems(cart).forEach((item) => {
        if (item.key) updates[item.key] = 0;
      });
      await this.updateCartLines(updates, fetchRequest);
    }

    cartHasQualifyingSubscription(cart) {
      if (!cart || !Array.isArray(cart.items)) return false;
      const eligibleVariantIds = new Set(Object.keys(this.config.variants || {}).map(String));
      return cart.items.some((item) => {
        const variantId = String(item && (item.variant_id || item.id) || '');
        return eligibleVariantIds.has(variantId) && Boolean(item && item.selling_plan_allocation);
      });
    }

    async reconcileInactiveGiftCart(pending, fetchRequest) {
      const cart = await this.getCart(fetchRequest);
      const markedItems = this.getMarkedGiftItems(cart);
      const mayKeepExistingGift = Boolean(
        pending
        && pending.mode === 'onetime'
        && pending.giftVariantId
        && this.cartHasQualifyingSubscription(cart)
      );
      const matchingItems = mayKeepExistingGift
        ? markedItems.filter((item) => (
          String(item.variant_id || item.id || '') === String(pending.giftVariantId)
        ))
        : [];
      const keptItem = matchingItems[0] || null;
      const updates = {};

      markedItems.forEach((item) => {
        if (!item.key) return;
        updates[item.key] = item === keptItem ? 1 : 0;
      });
      await this.updateCartLines(updates, fetchRequest);
      return Boolean(keptItem);
    }

    async validateMysteryGiftIsFree(variantId, fetchRequest) {
      const cart = await this.getCart(fetchRequest);
      const matchingItems = this.getMarkedGiftItems(cart).filter((item) => (
        String(item.variant_id || item.id || '') === String(variantId)
      ));
      if (matchingItems.length !== 1 || Number(matchingItems[0].quantity || 0) !== 1) return false;

      const gift = matchingItems[0];
      if (
        gift.final_line_price !== null
        && gift.final_line_price !== undefined
        && Number.isFinite(Number(gift.final_line_price))
      ) return Number(gift.final_line_price) === 0;
      if (
        gift.final_price !== null
        && gift.final_price !== undefined
        && Number.isFinite(Number(gift.final_price))
      ) return Number(gift.final_price) === 0;
      return false;
    }

    async removeDiscountCodes(codes, fetchRequest) {
      await this.applyPurchaseDiscounts([], fetchRequest, codes);
    }

    reportGiftIssue(message, detail) {
      const notice = this.querySelector('[data-gift-error]');
      if (notice) {
        notice.textContent = message;
        notice.hidden = false;
        notice.setAttribute('aria-hidden', 'false');
      }
      this.dispatchEvent(new CustomEvent('plan-picker:gift-error', {
        bubbles: true,
        detail: Object.assign({ message }, detail || {}),
      }));
    }

    clearGiftIssue() {
      const notice = this.querySelector('[data-gift-error]');
      if (notice) {
        notice.hidden = true;
        notice.setAttribute('aria-hidden', 'true');
      }
    }

    getCartApiRoot() {
      const configuredRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      const root = configuredRoot || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    collectDiscountCodes(cart) {
      const codes = [];
      const seen = new Set();
      const addCode = (value) => {
        const code = String(value || '').trim();
        const key = code.toLowerCase();
        if (!code || seen.has(key)) return;
        seen.add(key);
        codes.push(code);
      };
      const addApplication = (application) => {
        if (!application || String(application.type || '').toLowerCase() !== 'discount_code') return;
        addCode(application.title || application.code);
      };

      if (!cart || typeof cart !== 'object') return codes;
      if (Array.isArray(cart.discount_codes)) {
        cart.discount_codes.forEach((discount) => addCode(discount && (discount.code || discount.title)));
      }
      if (Array.isArray(cart.cart_level_discount_applications)) {
        cart.cart_level_discount_applications.forEach(addApplication);
      }
      if (Array.isArray(cart.items)) {
        cart.items.forEach((item) => {
          const allocations = []
            .concat(item.line_level_discount_allocations || [])
            .concat(item.discount_allocations || []);
          allocations.forEach((allocation) => addApplication(allocation && allocation.discount_application));
        });
      }

      return codes;
    }

    async applyPurchaseDiscounts(codes, fetchRequest, excludedCodes) {
      const root = this.getCartApiRoot();
      let existingCodes = [];

      try {
        const cartResponse = await fetchRequest(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (cartResponse.ok) existingCodes = this.collectDiscountCodes(await cartResponse.json());
      } catch (error) {
        console.warn('New Plan Picker: existing discount codes could not be read', error);
      }

      const excluded = new Set((Array.isArray(excludedCodes) ? excludedCodes : [excludedCodes])
        .map((code) => String(code || '').trim().toLowerCase())
        .filter(Boolean));
      existingCodes = existingCodes.filter((code) => !excluded.has(String(code || '').toLowerCase()));

      const requestedCodes = Array.isArray(codes) ? codes : [codes];
      requestedCodes.forEach((requestedCode) => {
        const code = String(requestedCode || '').trim();
        if (!code) return;
        if (!existingCodes.some((existingCode) => existingCode.toLowerCase() === code.toLowerCase())) {
          existingCodes.push(code);
        }
      });

      const updateResponse = await fetchRequest(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: existingCodes.join(',') }),
      });

      if (!updateResponse.ok) {
        throw new Error(`Shopify returned ${updateResponse.status} while applying the discount`);
      }
    }

    clampKids(value) {
      return Math.min(5, Math.max(1, Number.isFinite(value) ? value : 1));
    }

    parseBoolean(value) {
      if (typeof value === 'boolean') return value;
      return /^(true|1|yes|on)$/i.test(String(value || '').trim());
    }

    normalizeVariantId(value) {
      const normalized = String(value || '').trim();
      return /^\d+$/.test(normalized) && Number(normalized) > 0 ? normalized : '';
    }

    clampDiscountMinimum(value) {
      return Math.min(5, Math.max(2, Number.isFinite(value) ? value : 2));
    }

    getVariantId() {
      const variants = this.config.variants || {};
      const selectedId = this.variantInput && this.variantInput.value;
      if (selectedId && variants[String(selectedId)]) return String(selectedId);
      return Object.keys(variants)[0] || null;
    }

    getVariant() {
      const variantId = this.getVariantId();
      return variantId ? this.config.variants[variantId] : null;
    }

    syncVariantInput() {
      const variantId = this.getVariantId();
      if (!variantId || !this.variantInput) return variantId;

      if (String(this.variantInput.value) !== variantId) {
        this.variantInput.value = variantId;
        this.variantInput.setAttribute('value', variantId);
      }

      return variantId;
    }

    hasSellingPlan() {
      const variant = this.getVariant();
      return Boolean(variant && variant.sellingPlanId);
    }

    setKids(value) {
      this.kids = this.clampKids(value);
      this.quantityInput.value = String(this.kids);

      this.quantityButtons.forEach((button) => {
        const selected = Number(button.dataset.kids) === this.kids;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      const badgeTemplate = (this.config.quantityBadges || {})[String(this.kids)] || '';
      if (this.quantityBadge) {
        const badgeText = this.quantityBadge.querySelector('span');
        if (badgeText) badgeText.textContent = this.replaceQuantity(badgeTemplate);
        this.quantityBadge.classList.toggle('is-hidden', !badgeTemplate.trim());
        this.quantityBadge.classList.toggle('is-muted', this.kids === 1);
      }

      this.querySelectorAll('[data-template]').forEach((element) => {
        element.textContent = this.replaceQuantity(element.dataset.template || '');
      });

      this.updatePrices();
      this.dispatchEvent(new CustomEvent('plan-picker:quantity-change', {
        bubbles: true,
        detail: { quantity: this.kids },
      }));
    }

    blockUnavailableSubscription(event) {
      if (!this.subscriptionOnly || this.hasSellingPlan()) return false;

      if (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      if (this.planNotice) this.planNotice.hidden = false;
      return true;
    }

    setMode(mode) {
      let nextMode = this.subscriptionOnly
        ? 'subscription'
        : (mode === 'onetime' ? 'onetime' : 'subscription');
      if (!this.subscriptionOnly && nextMode === 'subscription' && !this.hasSellingPlan()) {
        nextMode = 'onetime';
      }
      this.mode = nextMode;

      this.purchaseChoices.forEach((choice) => {
        const selected = choice.dataset.purchaseChoice === this.mode;
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      if (this.onetimeDetails) {
        this.onetimeDetails.setAttribute('aria-hidden', this.mode === 'onetime' ? 'false' : 'true');
      }

      if (this.planNotice) {
        this.planNotice.hidden = this.subscriptionOnly
          ? this.hasSellingPlan()
          : (this.dataset.hasSellingPlan === 'true' || this.hasSellingPlan());
      }

      this.applyFormState();
      this.updatePrices();
      this.dispatchEvent(new CustomEvent('plan-picker:mode-change', {
        bubbles: true,
        detail: { mode: this.mode },
      }));
    }

    applyFormState() {
      if (!this.form || !this.quantityInput) return;
      this.quantityInput.value = String(this.kids || 1);

      this.syncVariantInput();
      const variant = this.getVariant();
      let sellingPlanInput = this.form.querySelector('[name="selling_plan"], .wk-selling-plan');

      window.__wkSubMode = this.mode;

      if (this.mode === 'subscription' && variant && variant.sellingPlanId) {
        if (!sellingPlanInput) {
          sellingPlanInput = document.createElement('input');
          sellingPlanInput.type = 'hidden';
          sellingPlanInput.name = 'selling_plan';
          sellingPlanInput.className = 'wk-selling-plan';
          this.form.appendChild(sellingPlanInput);
        }
        sellingPlanInput.disabled = false;
        sellingPlanInput.name = 'selling_plan';
        sellingPlanInput.value = String(variant.sellingPlanId);
      } else if (sellingPlanInput) {
        sellingPlanInput.disabled = true;
        sellingPlanInput.removeAttribute('name');
      }
    }

    handleVariantChange() {
      if (this.subscriptionOnly) this.setMode('subscription');
      else if (this.mode === 'subscription' && !this.hasSellingPlan()) this.setMode('onetime');
      else {
        this.applyFormState();
        this.updatePrices();
      }
    }

    replaceQuantity(text) {
      return String(text || '').replace(/\[quantity\]/gi, String(this.kids || 1));
    }

    parseManualPrice(value) {
      if (value === null || value === undefined) return null;
      let normalized = String(value).trim();
      if (!normalized) return null;

      normalized = normalized.replace(/\s/g, '');
      const lastComma = normalized.lastIndexOf(',');
      const lastDot = normalized.lastIndexOf('.');
      if (lastComma > lastDot) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = normalized.replace(/,/g, '');
      }
      normalized = normalized.replace(/[^0-9.-]/g, '');

      const number = Number(normalized);
      return Number.isFinite(number) ? Math.round(number * 100) : null;
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const currency = String(this.config.currency || 'USD').toUpperCase();
      const usePlainDollarSymbol = this.parseBoolean(this.config.plainDollarSymbol)
        || plainDollarCurrencies.has(currency);

      try {
        const numberOptions = {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        };

        if (usePlainDollarSymbol) {
          const number = new Intl.NumberFormat(
            this.config.locale || undefined,
            numberOptions
          ).format(amount);
          return `$${number}`;
        }

        return new Intl.NumberFormat(this.config.locale || undefined, {
          ...numberOptions,
          style: 'currency',
          currency,
        }).format(amount);
      } catch (error) {
        return usePlainDollarSymbol
          ? `$${amount.toFixed(2)}`
          : `${amount.toFixed(2)} ${currency}`.trim();
      }
    }

    updatePrices() {
      if (!this.config) return;
      const variant = this.getVariant();
      if (!variant) return;

      const manual = this.config.manual || {};
      const quantity = this.kids || 1;
      const deliveryDays = Math.max(1, Number(this.config.deliveryDays || 30));
      const configuredPrimaryDiscount = Math.min(
        100,
        Math.max(0, Number(this.config.primaryDiscountPercent || 0))
      );
      const configuredDiscount = Math.min(100, Math.max(0, Number(this.config.quantityDiscountPercent || 0)));
      const primaryDiscountFactor = 100 - configuredPrimaryDiscount;
      const quantityDiscountFactor = quantity >= this.quantityDiscountMinimum
        ? 100 - configuredDiscount
        : 100;
      const discountMultiplier = quantityDiscountFactor / 100;
      const calculateDiscountedPrice = (cents, applyPrimaryDiscount) => {
        const normalizedCents = Math.max(0, Math.round(Number(cents) || 0));
        const numerator = normalizedCents
          * (applyPrimaryDiscount ? primaryDiscountFactor : 100)
          * quantityDiscountFactor;
        return Math.floor((numerator + 9999) / 10000);
      };
      const applyDiscountWithCeiling = (cents, discountFactor) => {
        const normalizedCents = Math.max(0, Math.round(Number(cents) || 0));
        return Math.floor((normalizedCents * discountFactor + 99) / 100);
      };

      const manualSubscriptionUnit = this.parseManualPrice(manual.subscriptionPrice);
      const manualOnetimeUnit = this.parseManualPrice(manual.onetimePrice);
      const subscriptionUnit = manualSubscriptionUnit ?? Number(variant.sellingPrice || variant.price || 0);
      const subscriptionPerDayUnit = this.parseManualPrice(manual.subscriptionPerDay);
      const onetimeUnit = manualOnetimeUnit ?? Number(variant.price || 0);
      const onetimeCompareUnit = this.parseManualPrice(manual.onetimeComparePrice) ?? Number(variant.price || 0);
      const onetimePerDayUnit = this.parseManualPrice(manual.onetimePerDay);
      const subscriptionCompareUnit = this.parseManualPrice(manual.subscriptionComparePrice) ?? onetimeCompareUnit;

      const subscriptionPrice = calculateDiscountedPrice(
        subscriptionUnit,
        manualSubscriptionUnit === null
      );
      const onetimeAfterPrimaryDiscount = manualOnetimeUnit === null
        ? applyDiscountWithCeiling(onetimeUnit, primaryDiscountFactor)
        : manualOnetimeUnit;
      const onetimePrice = applyDiscountWithCeiling(
        onetimeAfterPrimaryDiscount,
        quantityDiscountFactor
      );
      const subscriptionPerDay = subscriptionPerDayUnit === null
        ? Math.round(subscriptionPrice / deliveryDays)
        : Math.round(subscriptionPerDayUnit * discountMultiplier);
      const onetimePerDay = onetimePerDayUnit === null
        ? Math.round(onetimePrice / deliveryDays)
        : Math.round(onetimePerDayUnit * discountMultiplier);

      const values = {
        subscription: {
          price: subscriptionPrice,
          compare: subscriptionCompareUnit > subscriptionPrice ? subscriptionCompareUnit : 0,
          perDay: subscriptionPerDay,
        },
        onetime: {
          price: onetimePrice,
          compare: onetimeCompareUnit > onetimePrice
            ? onetimeCompareUnit
            : (discountMultiplier < 1 ? onetimeUnit : 0),
          perDay: onetimePerDay,
        },
      };

      Object.entries(values).forEach(([mode, prices]) => {
        const priceElement = this.querySelector(`[data-price="${mode}"]`);
        const compareElement = this.querySelector(`[data-compare-price="${mode}"]`);
        const perDayElement = this.querySelector(`[data-per-day="${mode}"]`);

        if (priceElement) priceElement.textContent = this.formatMoney(prices.price);
        if (compareElement) {
          compareElement.textContent = prices.compare > 0 ? this.formatMoney(prices.compare) : '';
          compareElement.classList.toggle('is-hidden', prices.compare <= 0);
        }
        if (perDayElement) {
          perDayElement.textContent = `${this.formatMoney(prices.perDay)}${this.config.perDaySuffix || '/day'}`;
        }
      });
    }
  }

  customElements.define('new-plan-picker', NewPlanPicker);
}
